import { useEffect, useRef, useState } from "react";
import { View, TouchableOpacity, ScrollView, Animated, Easing, StyleSheet, Pressable, Modal } from "react-native";
import { MoreHorizontal, Users, Loader2, RefreshCw, Settings2, Trash2 } from "lucide-react";
import React from "react";
import { Plug, AlertTriangle, ShieldCheck, Zap } from "lucide-react";
import CustomText from "../../../common/general/CustomText/CustomText";
import { ConnectorCard } from "../ConnectorCard/ConnectorCard";
import { ConnectorLogo } from "../ConnectorDetails/ConnectorLogo";
import Pill from "../../../common/Pill/Pill";
import { PILL_TYPES } from "../ConnectorDetails/ConnectorDetails";
import { useToast } from "../../../../contexts/Toast/ToastProvider";
import {
  CatalogConnector,
  CONNECTOR_CATEGORY_LABELS,
  ConnectionStatus,
  ConnectorActionResult,
  ConnectorApprovalSummary,
  InstitutionalConnectionSummary,
} from "../catalog.utils";
import { ConfirmModal } from "../ConnectorDetails/ConfirmModal";

interface ActiveConnectionsTabProps {
  catalog: CatalogConnector[];
  connectionMap?: Map<string, InstitutionalConnectionSummary>;
  approvalMap?: Map<string, ConnectorApprovalSummary>;
  onSelect?: (id: string) => void;
  onAddConnection?: () => void;
  onRefresh?: () => Promise<void>;
  onDisconnect?: (
    connector: CatalogConnector,
    connection: InstitutionalConnectionSummary
  ) => Promise<ConnectorActionResult>;
  userConnectionAggregates?: UserConnectionAggregate[];
}

export interface UserConnectionAggregate {
  connectorId: string;
  totalConnections: number;
  activeConnections: number;
  failedConnections: number;
  status: Extract<ConnectionStatus, "working" | "expired" | "failed">;
  byRole: {
    faculty: number;
    student: number;
    admin: number;
  };
}

const GENERIC_ACTION_ERROR = "We were not able to complete this action.";
const DISCONNECT_ACTION_ERROR = "We were not able to disconnect this connector.";
const REFRESH_UNAVAILABLE_MESSAGE = "Manual refresh is not available from this screen yet.";
const STATUS_REFRESHED_MESSAGE = "Connector status refreshed.";

export default function ActiveConnectionTab({
  catalog,
  connectionMap,
  approvalMap,
  onSelect,
  onAddConnection,
  onRefresh,
  onDisconnect,
  userConnectionAggregates = [],
}: ActiveConnectionsTabProps) {
  const [disconnectTarget, setDisconnectTarget] = useState<InstitutionalConnectionSummary | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [menuVisible, setMenuVisible] = useState("");
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const toast = useToast();

  const spinAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!syncingId) return;

    spinAnim.setValue(0);
    const loop = Animated.loop(
      Animated.timing(spinAnim, {
        toValue: 1,
        duration: 1000,
        easing: Easing.linear,
        useNativeDriver: true,
      })
    );
    loop.start();

    return () => loop.stop();
  }, [syncingId, spinAnim]);

  const getConnector = (connectorId: string) => catalog.find((c) => c.id === connectorId);

  const activeCount = Array.from(connectionMap?.values() || []).filter((c) => c.status === "active").length;
  const alertCount = Array.from(connectionMap?.values() || []).filter(
    (c) => c.status === "failed" || c.status === "degraded"
  ).length;

  const totalUserConnections = userConnectionAggregates.reduce((sum, a) => sum + a.totalConnections, 0);

  const disconnectConnector = disconnectTarget ? getConnector(disconnectTarget.connectorId) : null;

  const runAction = async (callback: () => Promise<ConnectorActionResult>): Promise<ConnectorActionResult> => {
    try {
      const result = await callback();

      if (!result.success) {
        const message = result.message ?? GENERIC_ACTION_ERROR;
        return {
          success: false,
          message,
        };
      }

      return result;
    } catch (actionError) {
      const message = actionError instanceof Error ? actionError.message : GENERIC_ACTION_ERROR;
      return {
        success: false,
        message,
      };
    }
  };

  const handleConfirmDisconnect = async () => {
    if (!disconnectTarget || !disconnectConnector || !onDisconnect) {
      return;
    }

    setShowDisconnectConfirm(false);
    const connectorName = disconnectConnector.name;
    const result = await runAction(() => onDisconnect(disconnectConnector, disconnectTarget));
    setDisconnectTarget(null);

    if (result.success) {
      toast?.showToast({
        message: `${connectorName} has been disconnected.`,
        type: "success",
      });
    } else {
      toast?.showToast({
        message: result.message ?? DISCONNECT_ACTION_ERROR,
        type: "error",
      });
    }
  };

  const handleSyncNow = async (connectionId: string) => {
    if (!onRefresh) {
      toast?.showToast({ message: REFRESH_UNAVAILABLE_MESSAGE, type: "info" });
      return;
    }

    setSyncingId(connectionId);

    try {
      await onRefresh();
      toast?.showToast({ message: STATUS_REFRESHED_MESSAGE, type: "success" });
    } catch (refreshError) {
      toast?.showToast({
        message: refreshError instanceof Error ? refreshError.message : GENERIC_ACTION_ERROR,
        type: "error",
      });
    } finally {
      setSyncingId(null);
    }
  };

  if ((connectionMap?.size || 0) === 0 && (approvalMap?.size || 0) === 0 && userConnectionAggregates.length === 0) {
    return (
      <View style={styles.emptyState}>
        <Plug width={24} height={24} color="#667085" />
        <CustomText text="No active connections" style={styles.emptyTitle} useTranslationText={false} />
        <CustomText
          text="Set up your first connection to start syncing content from external services."
          style={styles.emptyDescription}
          useTranslationText={false}
        />
        <TouchableOpacity
          style={styles.primaryButton}
          onPress={() => onAddConnection?.()}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Add a new connection">
          <Plug width={16} height={16} color="#FFFFFF" />
          <CustomText text="New Connection" style={styles.primaryButtonText} useTranslationText={false} />
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.statsRow}>
        {[
          { value: connectionMap?.size || 0, label: "Institutional" },
          { value: activeCount, label: "Active" },
          { value: totalUserConnections, label: "User Connections" },
        ].map((stat, i) => (
          <View key={stat.label} style={styles.statItem}>
            {i > 0 && <View style={styles.divider} />}
            <View>
              <CustomText text={stat.value} style={styles.statValue} useTranslationText={false} />
              <CustomText text={stat.label} style={styles.statLabel} useTranslationText={false} />
            </View>
          </View>
        ))}
        {alertCount > 0 && (
          <>
            <View style={styles.divider} />
            <View style={styles.alertItem}>
              <AlertTriangle width={16} height={16} color="#B91C1C" />
              <View>
                <CustomText text={alertCount} style={styles.alertValue} useTranslationText={false} />
                <CustomText text="Needs Attention" style={styles.statLabel} useTranslationText={false} />
              </View>
            </View>
          </>
        )}
      </View>

      {(approvalMap?.size ?? 0) > 0 && (
        <View>
          <View style={styles.sectionHeader}>
            <ShieldCheck width={16} height={16} color="#667085" />
            <CustomText text="Approved Apps" style={styles.sectionTitle} useTranslationText={false} />
            <CustomText text="(users connect individually)" style={styles.sectionSubtitle} useTranslationText={false} />
          </View>
          <View style={styles.grid}>
            {Array.from(approvalMap?.values() || []).map((approval) => {
              const connector = getConnector(approval.connectorId);
              if (!connector) return null;
              return (
                <ConnectorCard
                  key={connector.id}
                  connector={connector}
                  approval={{
                    approvedBy: approval?.approvedBy || "",
                    userConnections: approval?.userConnections || 0,
                  }}
                  connectionStatus={approval.status ?? "approved"}
                  isApproved={approval.status === "approved"}
                  onClick={() => onSelect?.(approval.connectorId)}
                  style={styles.card}
                  selectedTab="activeConnections"
                />
              );
            })}
          </View>
        </View>
      )}
      {(connectionMap?.size ?? 0) > 0 && (
        <View>
          <View style={styles.sectionHeader}>
            <Zap width={16} height={16} color="#667085" />
            <CustomText text="Institutional Connections" style={styles.sectionTitle} useTranslationText={false} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeader}>
              <CustomText
                text="Service"
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="medium"
                role="text"
                style={[styles.headerCell, { flex: 2 }]}
              />
              <CustomText
                text="Status"
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="medium"
                role="text"
                style={[styles.headerCell, { flex: 1 }]}
              />
              <CustomText
                text="Last Sync"
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="medium"
                role="text"
                style={[styles.headerCell, { flex: 1.5 }]}
              />
              <CustomText
                text="Scope"
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="medium"
                role="text"
                style={[styles.headerCell, { flex: 1.5 }]}
              />
              <CustomText
                text="Users"
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="medium"
                role="text"
                style={[styles.headerCell, { flex: 0.8 }]}
              />
              <View style={{ width: 40 }} />
            </View>

            <ScrollView>
              {Array.from(connectionMap?.values() || []).map((connection, index) => {
                const connector = getConnector(connection.connectorId);
                if (!connector) return null;

                const isSyncing = syncingId === connection.id;

                return (
                  <TouchableOpacity
                    key={connection.id}
                    style={[styles.tableRow, index < (connectionMap?.size || 0) - 1 && styles.tableRowBorder]}
                    onPress={() => onSelect?.(connection.connectorId)}
                    activeOpacity={0.7}
                    accessibilityRole="button">
                    <View style={[styles.cell, { flex: 2 }]}>
                      <View style={styles.serviceCell}>
                        <ConnectorLogo iconKey={connector.icon} category={connector.category} size={36} />
                        <View>
                          <CustomText
                            text={connector.name || ""}
                            size="s"
                            useTranslationText={false}
                            textType="text"
                            weight="medium"
                            role="text"
                            style={styles.serviceName}
                          />
                          <CustomText
                            text={CONNECTOR_CATEGORY_LABELS[connector.category]}
                            size="xs"
                            useTranslationText={false}
                            textType="text"
                            weight="normal"
                            role="text"
                            style={styles.serviceCategory}
                          />
                        </View>
                      </View>
                    </View>

                    <View style={[styles.cell, { flex: 1 }]}>
                      <View style={{ alignItems: "flex-start" }}>
                        <Pill
                          text={PILL_TYPES[connection.status]?.text}
                          backgroundColor={PILL_TYPES[connection.status]?.backgroundColor}
                          textColor={PILL_TYPES[connection.status]?.textColor}
                          textSize="xs"
                          updated={true}
                          borderColor={PILL_TYPES[connection.status]?.borderColor}
                          borderWidth={1}
                          icon={PILL_TYPES[connection.status]?.icon}
                        />
                        {connection.errorMessage && !isSyncing && (
                          <CustomText
                            text={connection.errorMessage || ""}
                            size="xs"
                            useTranslationText={false}
                            textType="text"
                            weight="normal"
                            role="text"
                            style={styles.errorMessage}
                          />
                        )}
                      </View>
                    </View>

                    <View style={[styles.cell, { flex: 1.5 }]}>
                      <View style={styles.syncCell}>
                        {isSyncing && (
                          <Animated.View
                            style={{
                              transform: [
                                {
                                  rotate: spinAnim.interpolate({
                                    inputRange: [0, 1],
                                    outputRange: ["0deg", "360deg"],
                                  }),
                                },
                              ],
                            }}>
                            <Loader2 width={12} height={12} color="#2563EB" />
                          </Animated.View>
                        )}
                        <CustomText
                          text={isSyncing ? "Refreshing..." : connection.lastSyncAt || ""}
                          size="s"
                          useTranslationText={false}
                          textType="text"
                          weight="normal"
                          role="text"
                          style={styles.secondaryText}
                        />
                      </View>
                    </View>

                    <View style={[styles.cell, { flex: 1.5 }]}>
                      <CustomText
                        text={connection.scope || ""}
                        size="s"
                        useTranslationText={false}
                        textType="text"
                        weight="normal"
                        role="text"
                        style={styles.secondaryText}
                        numberOfLines={2}
                      />
                    </View>

                    <View style={[styles.cell, { flex: 0.8 }]}>
                      <View style={styles.usersCell}>
                        <Users width={14} height={14} color="#344054" />
                        <CustomText
                          text={String(connection?.userCount?.toLocaleString() || 0)}
                          size="s"
                          useTranslationText={false}
                          textType="text"
                          weight="normal"
                          role="text"
                          style={styles.secondaryText}
                        />
                      </View>
                    </View>

                    <TouchableOpacity
                      style={styles.actionsButton}
                      onPress={(e) => {
                        e.stopPropagation?.();
                      }}
                      accessibilityLabel={`Actions for ${connector.name}`}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                      <Pressable
                        onPress={(e) => {
                          const { pageX, pageY } = e.nativeEvent;
                          setMenuPosition({ x: pageX - 176, y: pageY + 4 });
                          setMenuVisible(connection.id);
                        }}
                        style={({ pressed }) => [styles.menuTrigger, pressed && { backgroundColor: "#f3f4f6" }]}
                        accessibilityLabel={`Actions for ${connector.name}`}
                        accessibilityRole="button">
                        <MoreHorizontal size={16} color="#6b7280" />
                      </Pressable>

                      {menuVisible === connection.id && (
                        <Modal transparent animationType="fade" onRequestClose={() => setMenuVisible("")}>
                          <Pressable style={styles.menuOverlay} onPress={() => setMenuVisible("")}>
                            <View style={[styles.menuContent, { top: menuPosition.y, right: 24 }]}>
                              <Pressable
                                style={({ pressed }) => [styles.menuItem, pressed && styles.menuItemPressed]}
                                onPress={() => {
                                  setMenuVisible("");
                                  onSelect?.(connection.connectorId);
                                }}>
                                <Settings2 size={16} color="#6b7280" />
                                <CustomText
                                  text="View Details"
                                  size="s"
                                  useTranslationText={false}
                                  textType="text"
                                  weight="normal"
                                  role="text"
                                  style={styles.menuItemText}
                                />
                              </Pressable>

                              {onRefresh ? (
                                <Pressable
                                  disabled={isSyncing}
                                  style={({ pressed }) => [
                                    styles.menuItem,
                                    pressed && styles.menuItemPressed,
                                    isSyncing && { opacity: 0.5 },
                                  ]}
                                  onPress={() => {
                                    setMenuVisible("");
                                    void handleSyncNow(connection.id);
                                  }}>
                                  <RefreshCw size={16} color={isSyncing ? "#d1d5db" : "#6b7280"} />
                                  <CustomText
                                    text={isSyncing ? "Refreshing..." : "Refresh Status"}
                                    size="s"
                                    useTranslationText={false}
                                    textType="text"
                                    weight="normal"
                                    role="text"
                                    style={[styles.menuItemText, isSyncing && { color: "#9ca3af" }]}
                                  />
                                </Pressable>
                              ) : null}

                              <View style={styles.menuSeparator} />

                              <Pressable
                                style={({ pressed }) => [styles.menuItem, pressed && styles.menuItemPressed]}
                                onPress={() => {
                                  setMenuVisible("");
                                  setDisconnectTarget(connection);
                                  setShowDisconnectConfirm(true);
                                }}>
                                <Trash2 size={16} color="#ef4444" />
                                <CustomText
                                  text="Disconnect"
                                  size="s"
                                  useTranslationText={false}
                                  textType="text"
                                  weight="normal"
                                  role="text"
                                  style={[styles.menuItemText, { color: "#ef4444" }]}
                                />
                              </Pressable>
                            </View>
                          </Pressable>
                        </Modal>
                      )}
                    </TouchableOpacity>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </View>
        </View>
      )}
      {userConnectionAggregates.length > 0 && (
        <View>
          <View style={styles.sectionHeader}>
            <Users width={16} height={16} color="#667085" />
            <CustomText text="User Connections" style={styles.sectionTitle} useTranslationText={false} />
            <CustomText text="(personal accounts)" style={styles.sectionSubtitle} useTranslationText={false} />
          </View>
          <View style={styles.grid}>
            {userConnectionAggregates.map((agg) => {
              const connector = getConnector(agg.connectorId);
              if (!connector) return null;

              const healthPct =
                agg.totalConnections > 0 ? Math.round((agg.activeConnections / agg.totalConnections) * 100) : 0;
              return (
                <ConnectorCard
                  key={connector.id}
                  connector={connector}
                  connectionStatus={agg.status}
                  isApproved={false}
                  onClick={() => onSelect?.(agg.connectorId)}
                  style={styles.card}
                  selectedTab="activeConnections-user"
                  healthPercentage={healthPct}
                  aggregate={agg}
                />
              );
            })}
          </View>
        </View>
      )}
      <ConfirmModal
        open={showDisconnectConfirm}
        onOpenChange={setShowDisconnectConfirm}
        onConfirm={handleConfirmDisconnect}
        title={`Disconnect ${disconnectConnector?.name ?? ""}?`}
        description={
          disconnectTarget && typeof disconnectTarget.userCount === "number"
            ? `This will disconnect ${disconnectConnector?.name ?? ""} for all ${disconnectTarget.userCount.toLocaleString()} users at your institution. Any previously imported content will remain, but live queries will stop immediately.`
            : `This will disconnect ${disconnectConnector?.name ?? ""} for your institution. Any previously imported content will remain, but live queries will stop immediately.`
        }
        confirmText="Disconnect"
        variant="destructive"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 32,
  },
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 48,
    gap: 12,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#101828",
    marginTop: 8,
  },
  emptyDescription: {
    fontSize: 14,
    color: "#667085",
    textAlign: "center",
    maxWidth: 280,
  },
  primaryButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    backgroundColor: "#2563EB",
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    width: "100%",
    marginTop: 8,
  },
  primaryButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
  statsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 24,
    padding: 16,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
  },
  statItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 24,
  },
  divider: {
    width: 1,
    height: 40,
    backgroundColor: "#E5E7EB",
  },
  statValue: {
    fontSize: 24,
    fontWeight: "600",
    color: "#101828",
  },
  statLabel: {
    fontSize: 12,
    color: "#667085",
  },
  alertItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  alertValue: {
    fontSize: 24,
    fontWeight: "600",
    color: "#B91C1C",
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#101828",
  },
  sectionSubtitle: {
    fontSize: 12,
    fontWeight: "400",
    color: "#667085",
  },
  card: {
    width: "32%",
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  tableContainer: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    overflow: "hidden",
  },
  tableHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#D1D5DB",
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerCell: {
    fontSize: 12,
    fontWeight: "500",
    color: "#667085",
  },
  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  tableRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  cell: {
    paddingRight: 8,
    justifyContent: "center",
  },
  serviceCell: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  serviceName: {
    fontSize: 14,
    fontWeight: "500",
    color: "#101828",
  },
  serviceCategory: {
    fontSize: 12,
    color: "#667085",
  },
  errorMessage: {
    fontSize: 12,
    color: "#B91C1C",
    marginTop: 4,
    maxWidth: 220,
  },
  syncCell: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },

  usersCell: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  secondaryText: {
    fontSize: 14,
    color: "#344054",
  },
  actionsButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  menuTrigger: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  menuOverlay: {
    flex: 1,
  },
  menuContent: {
    position: "absolute",
    width: 192,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    paddingVertical: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 8,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  menuItemPressed: {
    backgroundColor: "#f3f4f6",
  },
  menuItemText: {
    fontSize: 14,
    color: "#374151",
  },
  menuSeparator: {
    height: 1,
    backgroundColor: "#e5e7eb",
    marginVertical: 4,
  },
});
